import { create } from 'zustand'
import type {
  GraviActiveTask,
  GraviDeltaEvent,
  GraviStreamStatus,
  GraviToolCall,
} from './types'

/**
 * GraviChat streaming store. Ported from S&D
 * `components/_SiteWide/GraviChat/store.ts`.
 *
 * Holds the live streaming state for the currently-open thread: the running
 * status, the latest streamed text snapshot, the tool calls seen so far, and
 * the in-flight task. It is intentionally a plain (non-persisted) zustand store
 * — refresh recovery is handled via `sessionStorage` keyed by thread id (see
 * `persistActive`/`loadPersistedActive`), which the SSE stream hook writes on
 * every delta. This mirrors the Crossroads zustand convention in
 * `@/stores/tenantStore` while keeping ephemeral stream state out of storage.
 */

const SESSION_KEY_PREFIX = 'gravi.active.'

type GraviChatState = {
  status: GraviStreamStatus
  streamingText: string
  toolCalls: GraviToolCall[]
  resumedAt: number[]
  errorText: string | null
  // Last error from a non-stream mutation (submit turn, cancel, etc).
  // Surfaced as an inline red bubble in the chat so failures don't disappear
  // behind a dismissing toast.
  submitError: string | null
  lastCursor: number
  active: GraviActiveTask | null

  setStatus: (status: GraviStreamStatus) => void
  setActiveTask: (task: GraviActiveTask | null) => void
  setSubmitError: (text: string | null) => void
  onDelta: (evt: GraviDeltaEvent) => void
  resetStreamState: () => void
  reset: () => void
}

const initial = {
  status: 'idle' as GraviStreamStatus,
  streamingText: '',
  toolCalls: [] as GraviToolCall[],
  resumedAt: [] as number[],
  errorText: null as string | null,
  submitError: null as string | null,
  lastCursor: 0,
}

export const useGraviChatStore = create<GraviChatState>((set, get) => ({
  ...initial,
  active: null,

  setStatus: (status) => set({ status }),
  setActiveTask: (active) => set({ active }),
  setSubmitError: (submitError) => set({ submitError }),

  onDelta: (evt) => {
    set({ lastCursor: evt.cursor })
    switch (evt.kind) {
      case 'text_delta': {
        // gravitutor emits each text_delta as a full status snapshot (e.g.
        // "Using tool: Bash...", then "Processing... <partial answer>"), each
        // meant to REPLACE the current status line — not an incremental token.
        set({ streamingText: String(evt.data['text'] ?? '') })
        break
      }
      case 'tool_call': {
        set({
          toolCalls: [
            ...get().toolCalls,
            {
              cursor: evt.cursor,
              name: String(evt.data['name'] ?? ''),
              input: (evt.data['input'] as Record<string, unknown>) ?? {},
              duration_ms: evt.data['duration_ms'] as number | undefined,
              result_preview: evt.data['result_preview'] as string | undefined,
            },
          ],
        })
        break
      }
      case 'progress':
        break
      case 'resumed':
        set({ resumedAt: [...get().resumedAt, evt.cursor] })
        break
      case 'cancelled':
        set({ status: 'idle' })
        break
      case 'error':
        set({
          status: 'error',
          errorText: String(evt.data['message'] ?? 'unknown error'),
        })
        break
    }
  },

  resetStreamState: () =>
    set({
      streamingText: '',
      toolCalls: [],
      resumedAt: [],
      errorText: null,
      lastCursor: 0,
      status: 'idle',
    }),

  reset: () => set({ ...initial, active: null }),
}))

// ---------------------------------------------------------------------------
// sessionStorage refresh-recovery helpers (keyed per thread). The SSE stream
// hook persists `{ taskId, cursor }` on every delta; on remount the drawer can
// rehydrate the in-flight task and resume streaming from the last cursor.
// ---------------------------------------------------------------------------

type PersistedActive = { taskId: string; cursor: number }

export function persistActive(threadId: string, value: PersistedActive): void {
  try {
    sessionStorage.setItem(
      `${SESSION_KEY_PREFIX}${threadId}`,
      JSON.stringify(value),
    )
  } catch {
    // sessionStorage may be unavailable (private mode / SSR); recovery is
    // best-effort, so swallow.
  }
}

export function loadPersistedActive(threadId: string): PersistedActive | null {
  try {
    const raw = sessionStorage.getItem(`${SESSION_KEY_PREFIX}${threadId}`)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PersistedActive>
    if (
      typeof parsed.taskId === 'string' &&
      typeof parsed.cursor === 'number'
    ) {
      return { taskId: parsed.taskId, cursor: parsed.cursor }
    }
    return null
  } catch {
    return null
  }
}

export function clearPersistedActive(threadId: string): void {
  try {
    sessionStorage.removeItem(`${SESSION_KEY_PREFIX}${threadId}`)
  } catch {
    // best-effort
  }
}
